import { FC, useState } from 'react';
import { Wrapper } from './UseExistingPrebook.styles';

import {
  StaleContainer,
  StalePopup,
  Button,
  ButtonSecondary,
  StaleBtnGroup,
  StaleParagraphMedium,
  StaleHint,
  StaleInputRadio,
  Paragraph,
} from 'components';
import { useStoreActions, useStoreState } from 'state';
import { CONTRACT_STATUS, IInvoice, IRateContract } from 'types';
import { parseIntoCurrencyString, parseRateWithPrecision } from 'utils';
import dayjs from 'dayjs';
import _orderBy from 'lodash.orderby';

interface OwnProps {
  invoice: IInvoice;
  sellCurrency: string;
  onClose: () => void;
}

const UseExistingPrebook: FC<OwnProps> = ({
  invoice,
  sellCurrency,
  onClose,
}) => {
  const { rateContractsByCurrencyPair } = useStoreState(
    (state) => state.RateContractsState
  );
  const { bindContractRateToInvoice } = useStoreActions(
    (actions) => actions.InvoicesState
  );

  const rateContractsToUse = _orderBy(
    rateContractsByCurrencyPair(sellCurrency, invoice.currency).filter(
      (item) =>
        item.remainingBuyAmount >= (invoice.amountDue ?? 0) &&
        item.status === CONTRACT_STATUS.readyToUse &&
        !item.invoiceId &&
        dayjs(item.expiryDate).isAfter(dayjs().subtract(1, 'day'))
    ),
    'rate',
    'desc'
  );

  const [rateContract, setRateContract] = useState<IRateContract | null>(
    rateContractsToUse[0] || null
  );

  const onBindContractRateToInvoice = async () => {
    if (!rateContract) {
      return;
    }

    await bindContractRateToInvoice({
      contractId: rateContract.id,
      payload: {
        invoiceId: invoice.id,
      },
    });

    onClose();
  };

  return (
    <StalePopup
      title="Use prebooked amount?"
      theme="grey"
      width="439px"
      onClose={onClose}
      contentStyle={{
        minHeight: 'unset',
      }}
    >
      <Wrapper>
        <StaleContainer
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '24px 0 0',
          }}
        >
          <Paragraph
            style={{
              padding: '0 24px',
            }}
          >
            You have previously prebooked the rate for this currency pair. Would
            you like to use one of them for this invoice amount of{' '}
            {`${parseIntoCurrencyString(invoice.amountDue)} ${
              invoice.currency
            }`}
            ?
          </Paragraph>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              padding: '0 24px',
              flex: 1,
            }}
          >
            {rateContractsToUse.map((item) => (
              <StaleInputRadio
                id={item.id}
                key={item.id}
                label={
                  <div className="row">
                    <StaleParagraphMedium>{`1 ${
                      item.buyCurrency
                    } = ${parseRateWithPrecision(item.rate)} ${
                      item.sellCurrency
                    }`}</StaleParagraphMedium>
                    <div>
                      <StaleParagraphMedium>{`${parseIntoCurrencyString(
                        item.remainingBuyAmount
                      )} ${item.buyCurrency}`}</StaleParagraphMedium>
                      <StaleHint>{`pay by ${dayjs(item.expiryDate).format(
                        'DD MMM YY'
                      )}`}</StaleHint>
                    </div>
                  </div>
                }
                cardTransparent
                checked={item.id === rateContract?.id}
                onChange={() => {
                  setRateContract(item);
                }}
              />
            ))}
          </div>
        </StaleContainer>

        <StaleBtnGroup
          horizontal
          container={false}
          style={{
            paddingBottom: 32,
          }}
        >
          <ButtonSecondary onClick={onClose}>Not this time</ButtonSecondary>
          <Button
            disabled={!rateContract}
            onClick={onBindContractRateToInvoice}
          >
            Use prebooking
          </Button>
        </StaleBtnGroup>
      </Wrapper>
    </StalePopup>
  );
};

export default UseExistingPrebook;
